import React, { useEffect, useMemo, useState } from "react";
import { Formik, FormikValues, useFormikContext } from "formik";
import { navigate } from "gatsby";
import { RouteComponentProps } from "@reach/router";
import { useDispatch, useSelector } from "react-redux";

import Loader from "../../../components/atoms/loader";
import AlertList from "../../../components/organisms/alert-list";
import { ISurveyQuestion } from "../../../models/survey/survey-question.model";
import { TOption } from "../../../models/option.model";
import {
    checkFillSurvey,
    initFillSurvey,
    getFillSurveyPage,
    sendFillSurveyAnswers,
} from "../../../redux/survey-fill/survey-fill.actions";
import { FILL_SURVEY } from "../../../redux/survey-fill/survey-fill.reducer";
import {
    selectFillSurveyId,
    selectFillSurveyQuestionsByPageIndex,
} from "../../../redux/survey-fill/survey-fill.selectors";
import { selectEntityIsLoading } from "../../../redux/ui/ui.selectors";
import {
    selectUserApiStatus,
    selectUserLatestSurveySessionId,
} from "../../../redux/user/user.selectors";
import { genInitialValues, genValidationSchema } from "../../../utils/survey.utils";
import { useI18next } from "../../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";
import {
    STARTER_PROFILE_STEPS,
    getStarterProfileEntryMode,
    getNextMissingStarterProfilePage,
    getStarterProfileInitialStep,
    getStarterProfileProgress,
    getStarterProfileSessionAction,
    getStarterProfileCompletionPath,
    canLoadStarterProfilePages,
    getStarterClothingPreference,
    getSurveyClothingPreference,
    getStarterColorPalette,
    toggleStarterColorPreference,
    StarterColorPreference,
} from "../utils/starter-profile";
import {
    introPage,
    introShell,
    introHeroGrid,
    introCopy,
    introKicker,
    introTitle,
    introLead,
    introHighlights,
    introHighlight,
    introCta,
    introCtaArrow,
    introMicrocopy,
    introVisual,
    introHeroImage,
    introImageBadge,
    introTrustStrip,
    introTrustItem,
    introTrustIcon,
    introPreview,
    introPreviewHeading,
    introPreviewCopy,
    introPreviewGrid,
    introPreviewCard,
    introPreviewImage,
    introPreviewBody,
    introPreviewNumber,
    introBottomCta,
    page,
    shell,
    header,
    eyebrow,
    headerTitle,
    headerCopy,
    progressMeta,
    progressTrack,
    progressBar,
    card,
    stepLabel,
    stepTitle,
    stepDescription,
    questions,
    questionBlock,
    questionTitle,
    options,
    option,
    optionSelected,
    optionImage,
    optionCheck,
    colorPreferenceBlock,
    colorPreferenceIntro,
    colorPreferenceGrid,
    colorPreferenceCard,
    colorPreferenceHeader,
    colorPreferenceImage,
    colorPreferenceLabel,
    colorPreferenceActions,
    colorPreferenceButton,
    colorPreferenceLiked,
    colorPreferenceDisliked,
    noPreferenceActions,
    noPreferenceButton,
    noPreferenceSelected,
    errorText,
    actions,
    secondaryActions,
    backButton,
    saveButton,
    nextButton,
    completion,
    completionMark,
    completionTitle,
    completionCopy,
    completionList,
    generatorButton,
    dashboardLink,
} from "./starter-profile.module.scss";

const introHero = require("../../../assets/images/welcome-photo-landscape.jpg").default;
const stylePreview = require("../../../assets/images/beauty/beauty-hero.jpg").default;
const preferencesPreview = require("../../../assets/images/survey-start.jpg").default;
const sizesPreview = require("../../../assets/images/what-fits/reference/figure-hero-reference.jpg").default;
const budgetPreview = require("../../../assets/images/survey-end.jpg").default;

const REQUIRED_PAGE_INDEXES = Array.from(
    new Set(STARTER_PROFILE_STEPS.flatMap((step) => step.pageIndexes))
);
const GENERATOR_PATH = "/generator/wyczaruj-stylizacje";
const DASHBOARD_PATH = "/konto/panel/";
const STARTER_PROFILE_FORM_PATH = "/konto/profil-startowy/?start=1";

const INTRO_PREVIEWS = [
    { image: stylePreview, title: "Twój styl", copy: "Wybierzesz estetykę, w której czujesz się sobą." },
    { image: preferencesPreview, title: "Twoje preferencje", copy: "Zapiszesz ulubione ubrania i kolory." },
    { image: sizesPreview, title: "Twoje rozmiary", copy: "Podasz rozmiary góry, dołu i sukienek." },
    { image: budgetPreview, title: "Twój budżet", copy: "Ustalisz wygodne zakresy cenowe." },
];

export default function StarterProfile(props: RouteComponentProps) {
    const search = props.location?.search || "";
    const params = new URLSearchParams(search);
    const isEditing = params.get("edycja") === "1";

    if (getStarterProfileEntryMode(search) === "intro") {
        return <StarterProfileIntro />;
    }

    return <StarterProfileForm isEditing={isEditing} />;
}

function StarterProfileIntro() {
    return (
        <main className={introPage}>
            <div className={introShell}>
                <section className={introHeroGrid}>
                    <div className={introCopy}>
                        <p className={introKicker}>Twój styl. Twoje zasady.</p>
                        <h1 className={introTitle}>
                            Pozwól nam najpierw <em>poznać Ciebie.</em>
                        </h1>
                        <p className={introLead}>
                            Cztery krótkie kroki wystarczą, aby zapisać Twój styl, ulubione fasony,
                            rozmiary i budżet w jednym miejscu.
                        </p>
                        <ul className={introHighlights}>
                            <li className={introHighlight}><span aria-hidden="true">✓</span> Prosto i bez zgadywania</li>
                            <li className={introHighlight}><span aria-hidden="true">✓</span> Zmienisz odpowiedzi w każdej chwili</li>
                        </ul>
                        <button
                            className={introCta}
                            type="button"
                            onClick={() => navigate(STARTER_PROFILE_FORM_PATH)}
                        >
                            Zaczynam ustawiać profil
                            <span className={introCtaArrow} aria-hidden="true">→</span>
                        </button>
                        <p className={introMicrocopy}>Około 2 minut · bezpłatnie · zapis możesz później edytować</p>
                    </div>

                    <div className={introVisual}>
                        <img
                            className={introHeroImage}
                            src={introHero}
                            alt="Stylistka FashionFairy w różowej marynarce"
                        />
                        <div className={introImageBadge}>
                            <strong>4 kroki</strong>
                            <span>do Twojego profilu</span>
                        </div>
                    </div>
                </section>

                <section className={introTrustStrip} aria-label="Najważniejsze informacje">
                    <div className={introTrustItem}>
                        <span className={introTrustIcon} aria-hidden="true">♡</span>
                        <span><strong>Stworzone dla Ciebie</strong><small>Twoje wybory, Twój profil</small></span>
                    </div>
                    <div className={introTrustItem}>
                        <span className={introTrustIcon} aria-hidden="true">◷</span>
                        <span><strong>Tylko kilka minut</strong><small>Cztery konkretne kroki</small></span>
                    </div>
                    <div className={introTrustItem}>
                        <span className={introTrustIcon} aria-hidden="true">✓</span>
                        <span><strong>Postęp zapisany</strong><small>Możesz wrócić później</small></span>
                    </div>
                    <div className={introTrustItem}>
                        <span className={introTrustIcon} aria-hidden="true">↻</span>
                        <span><strong>Zawsze do zmiany</strong><small>Edytujesz z poziomu konta</small></span>
                    </div>
                </section>

                <section className={introPreview}>
                    <p className={introKicker}>Twój profil startowy</p>
                    <h2 className={introPreviewHeading}>Powiedz nam tylko to, co naprawdę ważne</h2>
                    <p className={introPreviewCopy}>
                        Bez długiej ankiety. Skupiamy się na czterech obszarach, które opisują Twoje potrzeby.
                    </p>
                    <div className={introPreviewGrid}>
                        {INTRO_PREVIEWS.map((item, index) => (
                            <article className={introPreviewCard} key={item.title}>
                                <img className={introPreviewImage} src={item.image} alt="" />
                                <div className={introPreviewBody}>
                                    <span className={introPreviewNumber}>0{index + 1}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.copy}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <section className={introBottomCta}>
                    <div>
                        <p className={introKicker}>Gotowa?</p>
                        <h2>Zacznijmy tworzyć profil dopasowany do Ciebie.</h2>
                    </div>
                    <button className={introCta} type="button" onClick={() => navigate(STARTER_PROFILE_FORM_PATH)}>
                        Przejdź do pierwszego kroku
                        <span className={introCtaArrow} aria-hidden="true">→</span>
                    </button>
                </section>
            </div>
        </main>
    );
}

function StarterProfileForm({ isEditing }: { isEditing: boolean }) {
    const dispatch = useDispatch();
    const { t } = useI18next();
    const sessionId = useSelector(selectFillSurveyId);
    const latestUserSessionId = useSelector(selectUserLatestSurveySessionId);
    const userApiStatus = useSelector(selectUserApiStatus);
    const isLoading = useSelector((state) => selectEntityIsLoading(state, FILL_SURVEY));
    const pageQuestions = useSelector((state) =>
        REQUIRED_PAGE_INDEXES.reduce<Record<number, ISurveyQuestion[] | null>>((collection, index) => {
            collection[index] = selectFillSurveyQuestionsByPageIndex(state, index);
            return collection;
        }, {})
    );
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [hasResumed, setHasResumed] = useState(false);

    useEffect(() => {
        const action = getStarterProfileSessionAction(sessionId, latestUserSessionId);
        if (action === "check") dispatch(checkFillSurvey());
        if (action === "create") dispatch(initFillSurvey());
    }, [dispatch, latestUserSessionId, sessionId]);

    const nextMissingPageIndex = getNextMissingStarterProfilePage(
        REQUIRED_PAGE_INDEXES,
        pageQuestions
    );

    useEffect(() => {
        if (!canLoadStarterProfilePages(sessionId, userApiStatus) || nextMissingPageIndex === null) return;
        dispatch(getFillSurveyPage(nextMissingPageIndex));
    }, [dispatch, nextMissingPageIndex, sessionId, userApiStatus]);

    const allRequiredQuestions = useMemo(
        () => REQUIRED_PAGE_INDEXES.flatMap((index) => pageQuestions[index] || []),
        [pageQuestions]
    );
    const allPagesLoaded = REQUIRED_PAGE_INDEXES.every((index) => Array.isArray(pageQuestions[index]));
    const persistedAnswers = useMemo(
        () =>
            allRequiredQuestions.reduce<Record<number, any>>((answers, question) => {
                answers[question.questionId] = question.answers;
                return answers;
            }, {}),
        [allRequiredQuestions]
    );

    useEffect(() => {
        if (!allPagesLoaded || hasResumed) return;
        setCurrentStepIndex(getStarterProfileInitialStep(persistedAnswers, isEditing));
        setHasResumed(true);
    }, [allPagesLoaded, hasResumed, isEditing, persistedAnswers]);

    const progress = getStarterProfileProgress(persistedAnswers);
    const stepCount = STARTER_PROFILE_STEPS.length;
    const isComplete = currentStepIndex >= STARTER_PROFILE_STEPS.length;
    const currentStep = STARTER_PROFILE_STEPS[currentStepIndex];
    const currentQuestions = currentStep
        ? currentStep.pageIndexes
            .flatMap((index) => pageQuestions[index] || [])
            .filter((question) => currentStep.questionIds.includes(question.questionId))
            .sort(
                (left, right) =>
                    currentStep.questionIds.indexOf(left.questionId) -
                    currentStep.questionIds.indexOf(right.questionId)
            )
        : [];

    const saveStep = (values: FormikValues) => {
        if (!currentStep) return;
        currentStep.pageIndexes.forEach((pageIndex) => {
            const payload = (pageQuestions[pageIndex] || []).reduce<Record<string, any>>(
                (result, question) => {
                    result[question.questionId] = values[question.questionId];
                    return result;
                },
                {}
            );
            dispatch(sendFillSurveyAnswers(payload, { index: pageIndex }));
        });
    };

    if (!sessionId || !allPagesLoaded || !hasResumed) {
        return (
            <main className={page}>
                <Loader position="fixed" />
                <p className={eyebrow}>Przygotowujemy Twój profil startowy…</p>
            </main>
        );
    }

    return (
        <main className={page}>
            <AlertList />
            <div className={shell}>

                {isComplete ? (
                    <section className={`${card} ${completion}`}>
                        <span className={completionMark} aria-hidden="true">✓</span>
                        <p className={eyebrow}>Profil startowy gotowy</p>
                        <h1 className={completionTitle}>Możemy wyczarować Twoją pierwszą stylizację</h1>
                        <p className={completionCopy}>
                            Zapisaliśmy Twój styl, preferencje, rozmiary i budżet. Pozostałą część profilu
                            uzupełnisz później — dokładnie wtedy, kiedy będziesz mieć na to ochotę.
                        </p>
                        <ul className={completionList}>
                            <li>Styl zapisany</li>
                            <li>Preferencje zapisane</li>
                            <li>Rozmiary zapisane</li>
                            <li>Budżet ustawiony</li>
                        </ul>
                        <button className={generatorButton} type="button" onClick={() => navigate(GENERATOR_PATH)}>
                            Wyczaruj pierwszą stylizację
                        </button>
                        <button className={dashboardLink} type="button" onClick={() => navigate(DASHBOARD_PATH)}>
                            Przejdź teraz do konta
                        </button>
                    </section>
                ) : (
                    <>
                        <header className={header}>
                            <p className={eyebrow}>{isEditing ? "Edycja profilu" : "Profil startowy"}</p>
                            <h1 className={headerTitle}>{isEditing ? "Zaktualizuj swój profil" : "Poznajmy Twój styl"}</h1>
                            <p className={headerCopy}>
                                {isEditing
                                    ? "Przejrzyj cztery kroki i zmień wybrane ustawienia."
                                    : "Cztery krótkie kroki wystarczą, żeby przygotować pierwszą stylizację."}
                            </p>
                            <div className={progressMeta}>
                                <span>Krok {currentStepIndex + 1} z {stepCount}</span>
                                <strong>{Math.max(progress, Math.round(((currentStepIndex + 1) / stepCount) * 100))}%</strong>
                            </div>
                            <div className={progressTrack} aria-label={`Postęp: krok ${currentStepIndex + 1} z ${stepCount}`}>
                                <span className={progressBar} style={{ width: `${((currentStepIndex + 1) / stepCount) * 100}%` }} />
                            </div>
                        </header>

                        <Formik
                            initialValues={genInitialValues(currentQuestions)}
                            validationSchema={genValidationSchema(currentQuestions, t)}
                            enableReinitialize
                            onSubmit={(values) => {
                                saveStep(values);
                                const completionPath = getStarterProfileCompletionPath(
                                    isEditing,
                                    currentStepIndex === stepCount - 1,
                                    sessionId
                                );
                                if (completionPath) {
                                    navigate(completionPath);
                                    return;
                                }
                                setCurrentStepIndex((index) => index + 1);
                            }}
                        >
                            {(formik) => (
                                <form className={card} onSubmit={formik.handleSubmit}>
                                    <p className={stepLabel}>{currentStep.label}</p>
                                    <h2 className={stepTitle}>{currentStep.title}</h2>
                                    <p className={stepDescription}>{currentStep.description}</p>

                                    <div className={questions}>
                                        {currentStep.id === "preferences" ? (
                                            <>
                                                {currentQuestions
                                                    .filter((question) => question.questionId !== 40 && question.questionId !== 23)
                                                    .map((questionData) => (
                                                        <StarterQuestion key={questionData.questionId} question={questionData} />
                                                    ))}
                                                <StarterColorPreferences
                                                    likedQuestion={currentQuestions.find((question) => question.questionId === 40)!}
                                                    dislikedQuestion={currentQuestions.find((question) => question.questionId === 23)!}
                                                />
                                            </>
                                        ) : (
                                            currentQuestions.map((questionData) => (
                                                <StarterQuestion key={questionData.questionId} question={questionData} />
                                            ))
                                        )}
                                    </div>

                                    {formik.submitCount > 0 && !formik.isValid ? (
                                        <p className={errorText}>
                                            {currentStep.id === "style"
                                                ? "Zaznacz jedną odpowiedź. Możesz wybrać tylko jeden styl."
                                                : currentStep.id === "preferences"
                                                    ? "Wybierz ubrania oraz co najmniej jedną opcję dla kolorów lub zaznacz brak preferencji."
                                                    : "Zaznacz po jednej odpowiedzi w każdym pytaniu, aby przejść dalej."}
                                        </p>
                                    ) : null}

                                    <div className={actions}>
                                        <div className={secondaryActions}>
                                            <button
                                                className={backButton}
                                                type="button"
                                                disabled={currentStepIndex === 0 || isLoading}
                                                onClick={() => {
                                                    saveStep(formik.values);
                                                    setCurrentStepIndex((index) => Math.max(index - 1, 0));
                                                }}
                                            >
                                                Wstecz
                                            </button>
                                            <button
                                                className={saveButton}
                                                type="button"
                                                disabled={!formik.isValid || isLoading}
                                                onClick={() => {
                                                    saveStep(formik.values);
                                                    navigate(DASHBOARD_PATH);
                                                }}
                                            >
                                                Zapisz i wyjdź
                                            </button>
                                        </div>
                                        <button className={nextButton} type="submit" disabled={isLoading}>
                                            {currentStepIndex === stepCount - 1 ? "Zapisz i zakończ" : "Dalej"}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </Formik>
                    </>
                )}
            </div>
        </main>
    );
}

function StarterQuestion({ question }: { question: ISurveyQuestion }) {
    const { values, setFieldValue, setFieldTouched } = useFormikContext<Record<string, any>>();
    const value = values[question.questionId];
    const isClothingPreference = question.questionId === 21;
    const isMulti = question.inputType === "checkbox" || isClothingPreference;
    const questionOptions = isClothingPreference
        ? (question.options || []).filter((optionData) => String(optionData.id ?? optionData.value) !== "38")
        : question.options || [];

    const choose = (option: TOption) => {
        const optionValue = option.id ?? option.value;
        if (isClothingPreference) {
            const selected = getStarterClothingPreference(value);
            const nextValue = selected.includes(String(optionValue))
                ? selected.filter((item) => item !== String(optionValue))
                : [...selected, optionValue];
            setFieldValue(
                String(question.questionId),
                getSurveyClothingPreference(nextValue)
            );
        } else if (isMulti) {
            const selected = Array.isArray(value) ? value : [];
            const nextValue = selected.map(String).includes(String(optionValue))
                ? selected.filter((item) => String(item) !== String(optionValue))
                : [...selected, optionValue];
            setFieldValue(String(question.questionId), nextValue);
        } else {
            setFieldValue(String(question.questionId), String(optionValue));
        }
        setFieldTouched(String(question.questionId), true);
    };

    return (
        <fieldset className={questionBlock}>
            <legend className={questionTitle}>{question.title}</legend>
            <div className={options}>
                {questionOptions.map((optionData) => {
                    const optionValue = optionData.id ?? optionData.value;
                    const selected = isClothingPreference
                        ? getStarterClothingPreference(value).includes(String(optionValue))
                        : Array.isArray(value)
                            ? value.map(String).includes(String(optionValue))
                            : String(value) === String(optionValue);
                    return (
                        <button
                            type="button"
                            key={String(optionValue)}
                            className={`${option} ${selected ? optionSelected : ""}`}
                            aria-pressed={selected}
                            onClick={() => choose(optionData)}
                        >
                            {optionData.imgUrl ? (
                                <img className={optionImage} src={optionData.imgUrl} alt="" />
                            ) : null}
                            <span>{optionData.label}</span>
                            <span className={optionCheck} aria-hidden="true">✓</span>
                        </button>
                    );
                })}
            </div>
        </fieldset>
    );
}

const asAnswerList = (value: any): Array<string | number> => Array.isArray(value) ? value : [];
const optionId = (option: TOption): string | number => option.id ?? option.value;
const isOptionSelected = (answers: Array<string | number>, id: string | number): boolean =>
    answers.some((answer) => String(answer) === String(id));
const findNoPreference = (optionsData: TOption[]): TOption | undefined =>
    optionsData.find((optionData) => optionData.label.trim().toLocaleUpperCase("pl-PL") === "BRAK PREFERENCJI");

function StarterColorPreferences({
    likedQuestion,
    dislikedQuestion,
}: {
    likedQuestion?: ISurveyQuestion;
    dislikedQuestion?: ISurveyQuestion;
}) {
    const { values, setFieldValue, setFieldTouched } = useFormikContext<Record<string, any>>();
    if (!likedQuestion || !dislikedQuestion) return null;

    const likedOptions = likedQuestion.options || [];
    const dislikedOptions = dislikedQuestion.options || [];
    const palette = getStarterColorPalette(likedOptions, dislikedOptions);
    const likedAnswers = asAnswerList(values[likedQuestion.questionId]);
    const dislikedAnswers = asAnswerList(values[dislikedQuestion.questionId]);
    const likedNoPreference = findNoPreference(likedOptions);
    const dislikedNoPreference = findNoPreference(dislikedOptions);
    const noPreference = likedNoPreference && dislikedNoPreference
        ? {
            likedOptionId: optionId(likedNoPreference),
            dislikedOptionId: optionId(dislikedNoPreference),
        }
        : undefined;

    const setAnswers = (
        nextLikedAnswers: Array<string | number>,
        nextDislikedAnswers: Array<string | number>
    ) => {
        setFieldValue(String(likedQuestion.questionId), nextLikedAnswers);
        setFieldValue(String(dislikedQuestion.questionId), nextDislikedAnswers);
        setFieldTouched(String(likedQuestion.questionId), true);
        setFieldTouched(String(dislikedQuestion.questionId), true);
    };

    const chooseColor = (
        color: (typeof palette)[number],
        preference: StarterColorPreference
    ) => {
        const nextAnswers = toggleStarterColorPreference(
            likedAnswers,
            dislikedAnswers,
            color,
            preference,
            noPreference
        );
        setAnswers(nextAnswers.likedAnswers, nextAnswers.dislikedAnswers);
    };

    const chooseNoPreference = (preference: StarterColorPreference) => {
        if (!noPreference) return;
        if (preference === "liked") {
            const selected = isOptionSelected(likedAnswers, noPreference.likedOptionId);
            setAnswers(selected ? [] : [noPreference.likedOptionId], dislikedAnswers);
            return;
        }
        const selected = isOptionSelected(dislikedAnswers, noPreference.dislikedOptionId);
        setAnswers(likedAnswers, selected ? [] : [noPreference.dislikedOptionId]);
    };

    const likedNoPreferenceSelected = noPreference
        ? isOptionSelected(likedAnswers, noPreference.likedOptionId)
        : false;
    const dislikedNoPreferenceSelected = noPreference
        ? isOptionSelected(dislikedAnswers, noPreference.dislikedOptionId)
        : false;

    return (
        <fieldset className={`${questionBlock} ${colorPreferenceBlock}`}>
            <legend className={questionTitle}>Twoje kolory</legend>
            <p className={colorPreferenceIntro}>
                Przy każdym kolorze wybierz „Lubię” albo „Nie lubię”. Jeden kolor nie może mieć obu oznaczeń.
            </p>
            <div className={colorPreferenceGrid}>
                {palette.map((color) => {
                    const liked = isOptionSelected(likedAnswers, color.likedOptionId);
                    const disliked = isOptionSelected(dislikedAnswers, color.dislikedOptionId);
                    return (
                        <article className={colorPreferenceCard} key={color.key}>
                            <div className={colorPreferenceHeader}>
                                {color.imgUrl ? (
                                    <img className={colorPreferenceImage} src={color.imgUrl} alt="" />
                                ) : null}
                                <span className={colorPreferenceLabel}>{color.label}</span>
                            </div>
                            <div className={colorPreferenceActions}>
                                <button
                                    type="button"
                                    className={`${colorPreferenceButton} ${liked ? colorPreferenceLiked : ""}`}
                                    aria-label={`${color.label}: lubię`}
                                    aria-pressed={liked}
                                    onClick={() => chooseColor(color, "liked")}
                                >
                                    <span aria-hidden="true">♥</span> Lubię
                                </button>
                                <button
                                    type="button"
                                    className={`${colorPreferenceButton} ${disliked ? colorPreferenceDisliked : ""}`}
                                    aria-label={`${color.label}: nie lubię`}
                                    aria-pressed={disliked}
                                    onClick={() => chooseColor(color, "disliked")}
                                >
                                    <span aria-hidden="true">×</span> Nie lubię
                                </button>
                            </div>
                        </article>
                    );
                })}
            </div>
            {noPreference ? (
                <div className={noPreferenceActions}>
                    <button
                        type="button"
                        className={`${noPreferenceButton} ${likedNoPreferenceSelected ? noPreferenceSelected : ""}`}
                        aria-pressed={likedNoPreferenceSelected}
                        onClick={() => chooseNoPreference("liked")}
                    >
                        Nie mam ulubionych kolorów
                    </button>
                    <button
                        type="button"
                        className={`${noPreferenceButton} ${dislikedNoPreferenceSelected ? noPreferenceSelected : ""}`}
                        aria-pressed={dislikedNoPreferenceSelected}
                        onClick={() => chooseNoPreference("disliked")}
                    >
                        Nie mam kolorów, których unikam
                    </button>
                </div>
            ) : null}
        </fieldset>
    );
}
